import React, { useEffect } from 'react';
import {
  UseFormReturn,
  useForm,
  UseFormProps,
  FormProvider,
  FieldValues,
  Mode,
  Resolver,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

export interface FormProps<T extends Record<string, unknown>>
  extends Pick<UseFormProps<T>, 'defaultValues' | 'values' | 'resolver'> {
  children: (props: Omit<UseFormReturn<T>, 'handleSubmit'>) => React.ReactNode;
  onSubmit?: (value: T) => void;
  validationSchema?: Yup.ObjectSchema<T>;
  mode?: Mode;
  onFormStateChange?: (isValid: boolean) => void;
}

const Form = <T extends FieldValues>({
  children,
  defaultValues,
  onSubmit,
  validationSchema,
  mode = 'onBlur',
  values,
  resolver,
  onFormStateChange,
  ...props
}: FormProps<T>) => {
  const methods = useForm<T>({
    resolver: (resolver ??
      (validationSchema && yupResolver(validationSchema))) as Resolver<T, any>,
    defaultValues,
    values,
    mode,
  });

  const { handleSubmit, formState, ...rest } = methods;
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(formState.isValid);
    }
  }, [formState.isValid, onFormStateChange]);
  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit && handleSubmit(onSubmit)} {...props}>
        {children(methods)}
      </form>
    </FormProvider>
  );
};

export default Form;
